import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'src/components/ui/button';
import { useToast } from 'src/hooks/use-toast';
import { CalendarIcon, Loader2, AlertTriangle } from 'lucide-react';
import { Label } from 'src/components/ui/label';
import { Input } from 'src/components/ui/input';
import { Calendar } from 'src/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/components/ui/popover';
import { format } from 'date-fns';
import AppRadioGroup from 'src/components/common/AppRadioGroup';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from 'src/components/ui/dialog';
import {
  useRequirementLocations,
  useAvailableFlows,
} from 'src/services/api/queries/opportunityQueries';

const formatDateToDDMMYYYY = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

// Flow rows haven't shipped to every environment yet, so normalize
// defensively rather than assuming one exact field naming.
const normalizeFlows = (raw) => {
  const list = raw?.data?.flows || raw?.data?.items || raw?.data || raw?.flows;
  if (!Array.isArray(list)) return [];
  return list
    .map((flow) => {
      const id = flow?.flow_id ?? flow?.id;
      if (id === undefined || id === null) return null;
      return {
        value: String(id),
        label: flow?.name || flow?.flow_name || flow?.title || `Flow ${id}`,
        description: flow?.description || flow?.summary || null,
      };
    })
    .filter(Boolean);
};

// CSE's direct "Forward to TA" action — opens a requirement's locations up
// for any active TA to see and submit against. No specific TA or TA Lead is
// targeted; the flow choice decides how sourcing proceeds from here.
const ForwardToTaModal = ({
  isOpen,
  onClose,
  onSubmit,
  opportunityId,
  deadline,
}) => {
  const { toast } = useToast();

  const {
    data: requirementLocationsResponse,
    isLoading: isRequirementLocationsLoading,
    isError: isRequirementLocationsError,
  } = useRequirementLocations(opportunityId, { enabled: isOpen });

  const {
    data: flowsResponse,
    isLoading: isFlowsLoading,
    isError: isFlowsError,
  } = useAvailableFlows(isOpen);

  const flows = useMemo(() => normalizeFlows(flowsResponse), [flowsResponse]);
  const requirementLocations =
    requirementLocationsResponse?.data?.requirement_locations || [];

  const estimatedDeadline =
    requirementLocationsResponse?.data?.requirement_info?.estimated_deadline;
  const effectiveDeadline = estimatedDeadline || deadline || '';
  const deadlineDateObj = effectiveDeadline
    ? new Date(effectiveDeadline)
    : null;

  const [dueDate, setDueDate] = useState(null);
  const [locationHeadcounts, setLocationHeadcounts] = useState({});
  const [selectedFlowId, setSelectedFlowId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deadlinePopoverOpen, setDeadlinePopoverOpen] = useState(false);

  const todayStart = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setDueDate(effectiveDeadline ? new Date(effectiveDeadline) : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, effectiveDeadline]);

  useEffect(() => {
    if (!requirementLocations.length) return;
    const defaults = {};
    requirementLocations.forEach((loc) => {
      const locId = loc?.requirement_location_id;
      defaults[locId] =
        loc?.pre_calculated_headcount !== undefined &&
        loc?.pre_calculated_headcount !== null
          ? String(loc.pre_calculated_headcount)
          : '0';
    });
    setLocationHeadcounts(defaults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requirementLocationsResponse]);

  // Pre-select the first flow once the list arrives, so the field isn't
  // left ambiguously blank when there's only ever going to be one choice.
  useEffect(() => {
    if (flows.length && !selectedFlowId) {
      setSelectedFlowId(flows[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flows]);

  const resetForm = () => {
    setDueDate(null);
    setLocationHeadcounts({});
    setSelectedFlowId('');
    setIsSubmitting(false);
  };

  const handleClose = () => {
    if (isSubmitting) return;
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!dueDate) {
      toast({
        message: 'Please select an assignment deadline',
        variant: 'warning',
      });
      return;
    }
    if (dueDate < todayStart) {
      toast({ message: 'Deadline cannot be in the past', variant: 'warning' });
      return;
    }
    if (flows.length && !selectedFlowId) {
      toast({ message: 'Please select a flow', variant: 'warning' });
      return;
    }
    if (!requirementLocations.length) {
      toast({
        message: 'No location data available to forward.',
        variant: 'warning',
      });
      return;
    }

    const locationsPayload = requirementLocations.map((loc) => {
      const locId = loc?.requirement_location_id || loc?.id;
      const value = locationHeadcounts[locId];
      const calculatedHeadcount = loc?.calculated_headcount ?? 0;
      const profilesSubmitted = loc?.profiles_submitted ?? 0;
      const minRequired = Math.max(0, calculatedHeadcount - profilesSubmitted);
      return {
        requirement_location_id: locId,
        candidates_requested: value ? Number(value) : null,
        minRequired,
        calculatedHeadcount,
        profilesSubmitted,
      };
    });

    const invalidLocation = locationsPayload.find(
      (loc) =>
        !loc.requirement_location_id ||
        loc.candidates_requested === null ||
        Number.isNaN(loc.candidates_requested) ||
        loc.candidates_requested < loc.minRequired
    );
    if (invalidLocation) {
      const locationName =
        requirementLocations.find(
          (l) =>
            (l?.requirement_location_id || l?.id) ===
            invalidLocation.requirement_location_id
        )?.location_name || 'this location';
      toast({
        message: `Candidates to source for ${locationName} must be at least ${invalidLocation.minRequired} (Required: ${invalidLocation.calculatedHeadcount}, Submitted: ${invalidLocation.profilesSubmitted})`,
        variant: 'warning',
      });
      return;
    }

    const payload = {
      requirement_locations: locationsPayload.map((loc) => ({
        requirement_location_id: loc.requirement_location_id,
        candidates_requested: loc.candidates_requested,
      })),
      assignment_deadline: formatDateToDDMMYYYY(dueDate),
      ...(selectedFlowId ? { flow_id: Number(selectedFlowId) } : {}),
    };

    try {
      setIsSubmitting(true);
      const isSuccess = await onSubmit(payload);
      if (isSuccess) {
        resetForm();
        onClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderRequirementLocationsContent = () => {
    if (isRequirementLocationsLoading) {
      return (
        <p className="text-xs text-muted-foreground mb-2">
          Loading headcount breakup...
        </p>
      );
    }
    if (isRequirementLocationsError) {
      return (
        <p className="text-xs text-destructive mb-2">
          Could not load location-wise headcount. Please try again.
        </p>
      );
    }
    if (!requirementLocations.length) {
      return (
        <p className="text-xs text-muted-foreground mb-2">
          No location headcount data available.
        </p>
      );
    }

    return (
      <div className="rounded-md border border-border bg-card shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_1.01fr] bg-muted/40 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">
          <span className="px-4 py-2">Location / Job Type</span>
          <span className="md:text-center px-4 py-2">Required Headcount</span>
          <span className="md:text-center px-4 py-2">Profiles Submitted</span>
          <span className="md:text-center px-4 py-2 bg-brand-bg-subtle text-primary">
            Candidates to Source
          </span>
        </div>
        <div className="divide-y divide-border">
          {requirementLocations.map((loc, index) => {
            const locationName = loc?.location_name;
            const employmentType = loc?.employment_type;
            const calculatedHeadcountValue = loc?.calculated_headcount ?? 0;
            const profileSubmittedValue = loc?.profiles_submitted ?? 0;
            const locId = loc?.requirement_location_id || loc?.id;
            const inputValue = locationHeadcounts[locId] ?? '';
            const minRequired = Math.max(
              0,
              calculatedHeadcountValue - profileSubmittedValue
            );

            return (
              <div
                key={locId || `${locationName}-${index}`}
                className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr_1fr_1.2fr] items-stretch gap-3 md:gap-2"
              >
                <div className="flex flex-col gap-1 px-4 py-2">
                  <span className="text-sm font-medium text-foreground">
                    {locationName}
                  </span>
                  {employmentType && (
                    <span className="text-[11px] text-muted-foreground">
                      {employmentType}
                    </span>
                  )}
                </div>
                <div className="flex items-center md:justify-center px-4 py-2">
                  <span className="text-base font-semibold text-ds-success">
                    {calculatedHeadcountValue}
                  </span>
                </div>
                <div className="flex items-center md:justify-center px-4 py-2">
                  <span className="text-base font-semibold text-foreground">
                    {profileSubmittedValue}
                  </span>
                </div>
                <div className="flex items-center justify-center px-4 py-2 bg-brand-bg-subtle text-foreground-strong">
                  <Input
                    type="number"
                    min={minRequired}
                    value={inputValue}
                    onChange={(e) =>
                      setLocationHeadcounts((prev) => ({
                        ...prev,
                        [locId]: e.target.value,
                      }))
                    }
                    className="h-9 w-16 border-input focus-visible:border-primary text-base font-medium text-foreground-strong"
                    placeholder="0"
                  />
                  {minRequired > 0 && (
                    <span className="text-[10px] text-muted-foreground ml-1">
                      (min: {minRequired})
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderFlowSection = () => {
    if (isFlowsLoading) {
      return (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading flows...
        </div>
      );
    }
    if (isFlowsError) {
      return (
        <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-500">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            Could not load assignment flows. You can still forward this
            requirement — it will be sourced without a specific flow.
          </span>
        </div>
      );
    }
    if (!flows.length) {
      return (
        <p className="text-xs text-muted-foreground">
          No assignment flows configured yet.
        </p>
      );
    }
    return (
      <AppRadioGroup
        name="assignment-flow"
        options={flows}
        value={selectedFlowId}
        onValueChange={setSelectedFlowId}
      />
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-[50rem]">
        <DialogHeader>
          <DialogTitle>Forward to TA</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-5">
          <p className="text-xs text-muted-foreground -mt-1">
            This opens the requirement&apos;s locations up for TA sourcing — any
            active TA can see it and submit candidates. There&apos;s no specific
            TA target and no TA Lead approval step.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Assignment Deadline</Label>
              <Popover
                open={deadlinePopoverOpen}
                onOpenChange={setDeadlinePopoverOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dueDate ? (
                      format(dueDate, 'PPP')
                    ) : (
                      <span>Select date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dueDate}
                    onSelect={(date) => {
                      setDueDate(date || null);
                      setDeadlinePopoverOpen(false);
                    }}
                    disabled={(date) => {
                      const max = deadlineDateObj
                        ? new Date(deadlineDateObj)
                        : null;
                      if (max) max.setHours(0, 0, 0, 0);
                      return date < todayStart || (max && date > max);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Flow</Label>
              {renderFlowSection()}
            </div>
          </div>

          {renderRequirementLocationsContent()}
        </DialogBody>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              isSubmitting ||
              isRequirementLocationsLoading ||
              !requirementLocations.length
            }
          >
            {isSubmitting ? 'Forwarding...' : 'Forward to TA'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ForwardToTaModal;
